import { Model } from "@vuex-orm/core";

export default class PasswordOptions extends Model {
  static entity = "password-options";

  static fields() {
    return {
      requiredLength: this.attr(0),
      requiredUpperCase: this.attr(false),
      requiredLowerCase: this.attr(false),
      requiredDigits: this.attr(0),
    };
  }

  // Checks if a character is strictly uppercase
  isUppercaseCharacter(char) {
    return char.match(new RegExp("^\\p{Lu}$", "u"));
  }

  // Checks if a character is strictly lowercase
  isLowercaseCharacter(char) {
    return char.match(new RegExp("^\\p{Ll}$", "u"));
  }

  // Checks if a character is strictly a digit
  isDigitCharacter(char) {
    return char.match(new RegExp("^\\p{N}$", "u"));
  }

  // Checks if a character is not a unicode letter or unicode number
  isSpecialCharacter(char) {
    return char.match(new RegExp(`^[^\\p{L}\\p{N}]$`, "u"));
  }

  // Checks if a character is a unicase language character
  // This means it is neither uppercase, nor lowercase and this
  // also excludes digits and special characters
  isUnicaseLanguageCharacter(char) {
    return (
      !this.isUppercaseCharacter(char) &&
      !this.isLowercaseCharacter(char) &&
      !this.isDigitCharacter(char) &&
      !this.isSpecialCharacter(char)
    );
  }

  hasUnicaseLanguageCharacters(password) {
    return [...password].some((char) => this.isUnicaseLanguageCharacter(char));
  }

  hasMinimalLength(password) {
    const lengthRegExp = new RegExp(`^.{${this.requiredLength},}$`);
    return !!password.match(lengthRegExp);
  }

  hasNumberOfUpperCase(password) {
    // Number of uppercase including unicase characters
    const numberOfUppercase = [...password].reduce(
      (count, char) =>
        count +
        (this.isUppercaseCharacter(char) ||
        this.isUnicaseLanguageCharacter(char)
          ? 1
          : 0),
      0
    );

    return numberOfUppercase >= this.requiredUpperCase;
  }

  hasNumberOfLowerCase(password) {
    // Number of lowercase including unicase characters
    const numberOfLowercase = [...password].reduce(
      (count, char) =>
        count +
        (this.isLowercaseCharacter(char) ||
        this.isUnicaseLanguageCharacter(char)
          ? 1
          : 0),
      0
    );

    return numberOfLowercase >= this.requiredLowerCase;
  }

  hasNumberOfDigits(password) {
    // Number of digits
    const numberOfDigits = [...password].reduce(
      (count, char) => count + (this.isDigitCharacter(char) ? 1 : 0),
      0
    );

    return numberOfDigits >= this.requiredDigits;
  }

  isPasswordValid(password) {
    return (
      this.hasMinimalLength(password) &&
      this.hasNumberOfUpperCase(password) &&
      this.hasNumberOfLowerCase(password) &&
      this.hasNumberOfDigits(password)
    );
  }

  /**
   * Returns the password options
   * @returns {Promise<PasswordOptions>}
   */
  static async getCurrentPasswordOptions() {
    let passwordOptions = PasswordOptions.query().first();

    if (!passwordOptions) {
      await PasswordOptions.fetchPasswordOptions().then(() => {
        passwordOptions = PasswordOptions.query().first();
      });
    }

    return passwordOptions;
  }

  /**
   * Refreshes the password options data from back-end
   */
  static async fetchPasswordOptions() {
    return await this.api().get("/account/password-options", {
      dataKey: "result",
      persistBy: "create",
    });
  }
}
